import type { ReactElement } from "react";
import { Button, StatusDot, type StatusTone } from "../atoms/index.js";
import { Alert } from "../molecules/index.js";
import type { CanvasConnectionState } from "./types.js";

/* ---------------- CanvasConnectionBanner ----------------
   Data-blind status surface for the collaboration (canvas) socket. Driven by a
   single `state` prop the host maps from its collab client; renders nothing when
   `connected`. Module-agnostic — SOLVE, VSM, and Hoshin canvases all reuse it.
   Scoped to the canvas socket only (the Player AI chat socket has its own
   status surface). */

interface BannerConfig {
  readonly tone: StatusTone;
  readonly label: string;
  /** Pulse the dot while a connection attempt is in flight. */
  readonly pulse: boolean;
  /** Whether a manual "Retry" can plausibly help (hidden for terminal states). */
  readonly recoverable: boolean;
}

const STATE_CONFIG: Record<
  Exclude<CanvasConnectionState, "connected">,
  BannerConfig
> = {
  connecting: {
    tone: "info",
    label: "Connecting to the live canvas…",
    pulse: true,
    recoverable: false,
  },
  reconnecting: {
    tone: "watch",
    label: "Reconnecting…",
    pulse: true,
    recoverable: true,
  },
  offline: {
    tone: "critical",
    label: "Can't connect to the live canvas",
    pulse: false,
    recoverable: true,
  },
  unavailable: {
    tone: "critical",
    label: "You don't have access to this canvas",
    pulse: false,
    recoverable: false,
  },
  live_tabs_warning: {
    tone: "watch",
    label: "Many live tabs are open.",
    pulse: false,
    recoverable: false,
  },
  size_warning: {
    tone: "watch",
    label: "This board is approaching its saved-size limit.",
    pulse: false,
    recoverable: false,
  },
  size_read_only: {
    tone: "critical",
    label: "This board reached its saved-size limit and is read-only.",
    pulse: false,
    recoverable: false,
  },
  write_rejected: {
    tone: "critical",
    label: "The board was restored to its last saved version.",
    pulse: false,
    recoverable: false,
  },
};

export interface CanvasConnectionBannerProps {
  /** Current collab-socket state. Renders `null` when `connected`. */
  readonly state: CanvasConnectionState;
  /**
   * Optional manual reconnect. A "Retry" action is shown only for recoverable
   * states (`reconnecting`, `offline`) when this handler is provided.
   */
  readonly onRetry?: (() => void) | undefined;
  readonly title?: string | undefined;
  readonly message?: string | undefined;
  readonly onDismiss?: (() => void) | undefined;
}

export function CanvasConnectionBanner({
  state,
  onRetry,
  title,
  message,
  onDismiss,
}: CanvasConnectionBannerProps): ReactElement | null {
  if (state === "connected") return null;
  const cfg = STATE_CONFIG[state];

  /* A floating Alert on the approved glass paint, pill corner (OK-37). It is
     a polite status, not an interrupting alert. The Retry and Dismiss
     buttons keep today's 20px box: Button xs is 24px (not in the row). */
  const actions =
    (cfg.recoverable && onRetry) || onDismiss ? (
      <>
        {cfg.recoverable && onRetry ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-1.5 py-0.5"
            onClick={onRetry}
          >
            Retry
          </Button>
        ) : null}
        {onDismiss ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-1.5 py-0.5"
            onClick={onDismiss}
          >
            Dismiss
          </Button>
        ) : null}
      </>
    ) : undefined;

  return (
    <Alert
      variant="floating"
      surface="glass"
      role="status"
      aria-live="polite"
      data-testid="canvas-connection-banner"
      icon={<StatusDot tone={cfg.tone} pulse={cfg.pulse} size="sm" />}
      title={title}
      actionsSlot={actions}
    >
      {message ?? cfg.label}
    </Alert>
  );
}
